import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircle, ShoppingBag, Truck, Wallet, Menu, X, Zap,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../redux/store";
import logo from "../../assets/app/logo.png";
import ClientProductOrders from "./ClientProductOrders";



/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:   "#08060F",
  sidebar:  "#0B0916",
  topbar:   "rgba(11,9,22,0.97)",
  cardBg:   "#100D1C",
  surface:  "#1A1530",
  grad:     "linear-gradient(90deg,#007BFF 0%,#6A00FF 50%,#FF00D4 100%)",
  gradAnim: "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF)",
  blue:     "#007BFF",
  purple:   "#6A00FF",
  pink:     "#FF00D4",
  white:    "#FFFFFF",
  offwhite: "#E8E4FF",
  muted:    "#8B82A8",
  mutedDim: "#5A5278",
  border:   "rgba(106,0,255,0.18)",
  borderHi: "rgba(106,0,255,0.45)",
};

const GRAD = T.grad;
const GA   = T.gradAnim;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,::before,::after{box-sizing:border-box;margin:0;padding:0;}
  body{background:#08060F;}
  button,input{font-family:inherit;}
  ::-webkit-scrollbar{width:3px;}
  ::-webkit-scrollbar-thumb{background:rgba(106,0,255,.25);border-radius:99px;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes shimmer{0%{background-position:-200% center;}100%{background-position:200% center;}}
  @keyframes pulseDot{0%,100%{opacity:1;}50%{opacity:.4;}}

  /* ── sidebar nav button ── */
  .sd-nav{
    width:100%;display:flex;align-items:center;gap:10px;
    padding:11px 16px;border:none;background:transparent;
    cursor:pointer;transition:all .18s;font-family:inherit;
    border-left:2px solid transparent;text-align:left;
  }
  .sd-nav:hover{background:rgba(106,0,255,.08);}
  .sd-nav.on{
    background:rgba(106,0,255,.12);
    border-left-color:#6A00FF;
  }

  /* ── icon button ── */
  .sd-icon{
    width:34px;height:34px;border:none;
    background:transparent;display:flex;align-items:center;
    justify-content:center;cursor:pointer;transition:all .15s;
    border-radius:8px;border:1px solid transparent;
  }
  .sd-icon:hover{background:rgba(106,0,255,.12);border-color:rgba(106,0,255,.35);}
`;

/* ─── nav config ─── */
type TabKey = "add" | "orders" | "delivery" | "payout";
const NAV: { id: TabKey; label: string; icon: React.ElementType }[] = [
  //{ id: "add",      label: "Products",       icon: PlusCircle  },
  { id: "orders",   label: "Orders",         icon: ShoppingBag },
 // { id: "delivery", label: "Delivery Zones", icon: Truck       },
 // { id: "payout",   label: "Payouts",        icon: Wallet      },
];

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const params   = useParams<{ userId?: string }>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const userId   = params.userId || authUser?.userId;

  const [activeTab, setActiveTab] = useState<TabKey>("add");
  const [mobile,    setMobile]    = useState(window.innerWidth < 1024);
  const [sidebar,   setSidebar]   = useState(false);

  useEffect(() => {
    if (!authUser)   { navigate("/login");  return; }
    if (!userId)     { navigate("/"); }
  }, [authUser, userId, navigate]);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const navClick = (id: TabKey) => {
    setActiveTab(id);
    if (mobile) setSidebar(false);
  };

  const renderMain = () => {
    switch (activeTab) {
      case "orders":   return <ClientProductOrders />;
  
      default:         return null;
    }
  };

  const initial = authUser?.email?.charAt(0)?.toUpperCase() ?? "M";

  /* ═══ RENDER ═══ */
  return (
    <div style={{
      minHeight: "100vh", background: T.pageBg,
      fontFamily: "'Plus Jakarta Sans',sans-serif",
      color: T.offwhite, display: "flex",
    }}>
      <style>{CSS}</style>

      {/* ── mobile overlay ── */}
      <AnimatePresence>
        {mobile && sidebar && (
          <motion.div
            key="ov"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebar(false)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:998, backdropFilter:"blur(6px)" }}
          />
        )}
      </AnimatePresence>

      {/* ══════════════════════════
          SIDEBAR
      ══════════════════════════ */}
      <aside style={{
        width: 230,
        background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
        display: "flex", flexDirection: "column",
        position: mobile ? "fixed" : "sticky",
        top: 0, height: "100vh", zIndex: 999,
        transform: mobile && !sidebar ? "translateX(-100%)" : "translateX(0)",
        transition: "transform .3s cubic-bezier(.4,0,.2,1)",
        boxShadow: mobile && sidebar ? "6px 0 40px rgba(106,0,255,.18)" : "none",
        flexShrink: 0,
      }}>
        {/* gradient top accent */}
        <div style={{ height: 3, background: GA, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite" }} />

        {/* ── logo block ── */}
        <div style={{
          padding: "18px 18px 16px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {/* logo mark with gradient ring */}
            <div style={{
              position: "relative", width: 32, height: 32,
              borderRadius: "50%", padding: 2,
              background: GRAD, backgroundSize: "200% 100%",
              animation: "gradSpin 4s linear infinite",
              flexShrink: 0,
            }}>
              <div style={{
                width: "100%", height: "100%", borderRadius: "50%",
                background: T.sidebar, overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <img src={logo} alt="Metarelm" style={{ width: "80%", height: "80%", objectFit: "contain" }} />
              </div>
            </div>

            {/* wordmark */}
            <div>
              <div style={{
                fontSize: 13, fontWeight: 900, letterSpacing: 1.5,
                background: GA, backgroundSize: "300% 100%",
                animation: "shimmer 4s linear infinite",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
              }}>
                METARELM
              </div>
              <div style={{ fontSize: 8, color: T.mutedDim, letterSpacing: "0.18em", marginTop: 1 }}>
                SELLER STUDIO
              </div>
            </div>
          </div>

          {mobile && (
            <button className="sd-icon" onClick={() => setSidebar(false)}>
              <X size={15} color={T.muted} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* ── navigation ── */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "10px 0" }}>
          {NAV.map(({ id, label, icon: Icon }) => {
            const on = activeTab === id;
            return (
              <button key={id} className={`sd-nav${on ? " on" : ""}`} onClick={() => navClick(id)}>
                {/* active: gradient icon bg dot */}
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: on
                    ? "linear-gradient(135deg,rgba(0,123,255,.25),rgba(106,0,255,.25))"
                    : "rgba(255,255,255,.04)",
                  border: `1px solid ${on ? "rgba(106,0,255,.4)" : "rgba(255,255,255,.06)"}`,
                  transition: "all .18s",
                }}>
                  <Icon size={14}
                    color={on ? T.blue : T.mutedDim}
                    strokeWidth={on ? 2 : 1.6} />
                </div>

                <span style={{
                  fontSize: 12, fontWeight: on ? 700 : 500,
                  color: on ? T.offwhite : T.muted,
                  flex: 1, letterSpacing: on ? -0.1 : 0,
                  transition: "all .15s",
                }}>
                  {label}
                </span>

                {/* active pill */}
                {on && (
                  <div style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: GRAD, backgroundSize: "200% 100%",
                    animation: "gradSpin 3s ease infinite, pulseDot 2s infinite",
                    flexShrink: 0,
                  }} />
                )}
              </button>
            );
          })}
        </nav>

        {/* ── sidebar footer ── */}
        <div style={{ padding: "14px 18px", borderTop: `1px solid ${T.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {/* avatar */}
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              background: GRAD, backgroundSize: "200% 100%",
              animation: "gradSpin 4s linear infinite",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{initial}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.offwhite, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {authUser?.firstName || authUser?.displayName || "Seller"}
              </div>
              <div style={{ fontSize: 9, color: T.mutedDim, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {authUser?.email || ""}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* ══════════════════════════
          MAIN CONTENT
      ══════════════════════════ */}
      <div style={{ flex: 1, display:"flex", flexDirection:"column", minWidth: 0 }}>

        {/* ── topbar ── */}
        <header style={{
          height: 54, background: T.topbar,
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 20px",
          position: "sticky", top: 0, zIndex: 100, flexShrink: 0,
        }}>
          {/* left side */}
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {mobile && (
              <button className="sd-icon" onClick={() => setSidebar(true)}>
                <Menu size={16} color={T.muted} strokeWidth={1.5} />
              </button>
            )}

            {/* active tab label with gradient accent */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:3, height:18, borderRadius:99, background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite" }} />
              <h1 style={{ fontSize: 15, fontWeight: 800, color: T.offwhite, letterSpacing: -0.2 }}>
                {NAV.find(n => n.id === activeTab)?.label ?? "Dashboard"}
              </h1>
            </div>
          </div>

          {/* right side */}
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {/* live indicator */}
            <div style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:999, background:"rgba(0,230,118,.1)", border:"1px solid rgba(0,230,118,.25)" }}>
              <div style={{ width:5, height:5, borderRadius:"50%", background:"#00E676", animation:"pulseDot 2s infinite" }} />
              <span style={{ fontSize:9, fontWeight:800, color:"#00E676", letterSpacing:"0.1em" }}>LIVE</span>
            </div>

            {/* avatar */}
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: GRAD, backgroundSize: "200% 100%",
              animation: "gradSpin 4s linear infinite",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}>
              <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{initial}</span>
            </div>
          </div>
        </header>

        {/* ── page content ── */}
        <main style={{
          flex: 1, overflowY: "auto",
          background: T.pageBg,
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.2, 0, 0.1, 1] }}
              style={{ minHeight: "100%" }}
            >
              {renderMain()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ClientDashboard;